import { Text } from "@smoothincorg/smooth-ui"
import Link from "next/link"
import { useRouter } from "next/router"
import React, { useEffect } from "react"
import { useForm } from "react-hook-form"
import { BiChat, BiRocket } from "react-icons/bi"
import { BorderedButton } from "src/components/Button"
import { Card } from "src/components/page-directory/Card"
import { formatDate } from "src/components/List/List"
import { NO_AVATAR_PATH } from "src/features/chat/components/Message"
import { Proposal as ProposalType } from "src/hooks/useProposalSWR"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { useUserSWR } from "src/hooks/useUserSWR"
import { camelCase, snakeCase } from "src/lib/aikagi"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants"
import { SMOOTH_LINE_API_URL } from "src/lib/constants/value"
import useSWR from "swr"
import { useEditingId } from "../../hooks/useEditingId"
import { ModalEditableForm } from "../ModalEditableForm/ModalEditableForm"
import { Proposal, StatusEditor, StatusItems } from "../Proposal/Proposal"
import * as styles from "./User.css"
import { InputField } from "src/components/InputField"
import { useNotification } from "@smoothincorg/smooth-ui"

type Props = {
  className?: string
  userId: number
}

export const sortById = <T extends { id: number }[]>(array: T) => {
  const newArray = [...array]
  return newArray.sort((a, b) => b.id - a.id)
}

const USER_STATUSES = [
  { id: 1, content: "follow", content_jp: "LINEアカウントをフォローしている" },
  {
    id: 2,
    content: "unfollow_block",
    content_jp: "LINEアカウントをアンフォローしているかブロックしている",
  },
  { id: 32, content: "blacklist", content_jp: "ブラックリストに入っている" },
  { id: 64, content: "pre_registered", content_jp: "先行申し込みユーザー" },
  { id: 128, content: "agreement", content_jp: "約款確認済み" },
  { id: 256, content: "launch_user", content_jp: "ローンチ初回ユーザー" },
  { id: 512, content: "refused", content_jp: "拒否希望ユーザー" },
  { id: 1024, content: "v2_agreement", content_jp: "内見前本審査規約同意済み" },
  { id: 2048, content: "out_of_service", content_jp: "対応エリア外" },
  {
    id: 8192,
    content: "v3_agreement",
    content_jp: "金利ありの個別信用購入あっせん契約申込書・個別信用購入あっせん約款同意済み",
  },
  {
    id: 16384,
    content: "v2023dec_agreement",
    content_jp: "V3にクロスセル対応のための規約同意が追加されたもの",
  },
]

export type UserAuthType = {
  id: number
  email: string
  enabled: boolean
  freshToken: string
  provider: string
  telephoneNumber: string
  telephoneNumber2: string
  token: string
  createdAt: string
  updatedAt: string
  lineUserId: string
  userId: number
}

type MailGroupCreationRequestType = {
  id: number
  email: string
}

const userDefaultValues = {
  status: 0,
  lastFurigana: "",
  firstFurigana: "",
  lastName: "",
  firstName: "",
}

export const User: React.FC<Props> = ({ userId }) => {
  const { getWithToken, patchWithToken } = useSmoothApi()
  const userSWR = useUserSWR(userId)
  const router = useRouter()
  const isOpeningModal = router.asPath.includes("edit")
  const proposalResourceName = `Proposal`
  const { setEditingId } = useEditingId(proposalResourceName)
  const { handleSubmit, setValue, watch, reset, register } = useForm({
    defaultValues: userDefaultValues,
  })

  const statusValue = watch("status")

  const usersUserAuthSWR = useSWR(
    `${SMOOTH_SMOOTHER_API_URL}/users/${userId}/user_auth`,
    async (url) => {
      const response = await getWithToken(url)
      return camelCase(response.data["data"]) as UserAuthType
    }
  )

  const proposalsSWR = useSWR(
    `${SMOOTH_SMOOTHER_API_URL}/users/${userId}/proposals`,
    async (url) => {
      const response = await getWithToken(url)
      return response.data["data"].map((d: ProposalType) => camelCase(d)) as ProposalType[]
    }
  )

  const mailGroupCreationRequestsSWR = useSWR(
    `${SMOOTH_SMOOTHER_API_URL}/users/${userId}/mail_group_creation_request`,
    async (url) => {
      const response = await getWithToken(url)
      return camelCase(response.data["data"]) as MailGroupCreationRequestType
    }
  )

  const mutateProposals = async () => {
    proposalsSWR.mutate()
  }

  const { showAlert, showSnackbar } = useNotification()

  useEffect(() => {
    if (userSWR.data) {
      reset({
        status: userSWR.data.status,
        lastFurigana: userSWR.data.lastFurigana,
        firstFurigana: userSWR.data.firstFurigana,
        lastName: userSWR.data.lastName,
        firstName: userSWR.data.firstName,
      })
    }
  }, [reset, userSWR.data])

  return (
    <div className={styles.root}>
      <div
        style={{
          width: isOpeningModal ? "70%" : "auto",
          maxWidth: "1200px",
          marginLeft: isOpeningModal ? "5%" : "auto",
          marginRight: "auto",
        }}
      >
        <section className={styles.section}>
          <Card className={styles.rowList}>
            <ModalEditableForm
              opeHref={`${SMOOTH_LINE_API_URL}/admin/users/${userId}`}
              resourceName="User"
              resource={userSWR.data}
              items={[
                [
                  "",
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    key={userSWR.data?.iconSrc}
                    alt="icon"
                    src={userSWR.data?.iconSrc}
                    style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                      border: "1px solid #ccc",
                    }}
                    onError={(e) => {
                      if (e.target instanceof HTMLImageElement) {
                        e.target.src = NO_AVATAR_PATH
                      }
                    }}
                  />,
                ],
                ["lastFurigana", userSWR.data?.lastFurigana],
                ["firstFurigana", userSWR.data?.firstFurigana],
                ["lastName", userSWR.data?.lastName],
                ["firstName", userSWR.data?.firstName],
                [
                  "Status",
                  <StatusItems
                    key={userSWR.data?.status}
                    targetStatus={userSWR.data?.status}
                    statuses={USER_STATUSES}
                  />,
                ],
                ["lineUserId", usersUserAuthSWR?.data?.lineUserId],
                ["lineUserName", userSWR.data?.lineUserName],
                ["telephoneNumber", usersUserAuthSWR.data?.telephoneNumber],
                ["createdAt", formatDate(userSWR.data?.createdAt)],
                ["updatedAt", formatDate(userSWR.data?.updatedAt)],
                ["メールグループ作成対象メールアドレス", mailGroupCreationRequestsSWR?.data?.email],
              ]}
              onSubmit={handleSubmit(async (values) => {
                const userRes = await patchWithToken(`${SMOOTH_SMOOTHER_API_URL}/users/${userId}`, {
                  user: snakeCase(values),
                })

                if (userRes.status === 200) {
                  showSnackbar("更新しました", "success")
                }

                userSWR.mutate()
              })}
            >
              <StatusEditor
                label="ステータス"
                statuses={USER_STATUSES}
                value={statusValue}
                style={{ marginBottom: 30 }}
                onChange={(e) => {
                  const currentStatus = statusValue
                  const changedStatus = parseInt(e.target.value)
                  const hasStatusAlready = currentStatus & changedStatus

                  const newStatus = hasStatusAlready
                    ? currentStatus - changedStatus
                    : currentStatus + changedStatus

                  setValue("status", newStatus)
                }}
              />
              <InputField label="姓（漢字）" className="mb-4" {...register("lastName")} />
              <InputField label="名（漢字）" className="mb-4" {...register("firstName")} />
              <InputField label="姓（カナ）" className="mb-4" {...register("lastFurigana")} />
              <InputField label="名（カナ）" className="mb-4" {...register("firstFurigana")} />
              <BorderedButton
                type="submit"
                style={{
                  margin: "20px 0",
                  width: "100%",
                  height: 50,
                  fontSize: 16,
                  fontWeight: "bold",
                }}
              >
                保存
              </BorderedButton>
            </ModalEditableForm>
            <a
              href={`/admin/user-auths/${usersUserAuthSWR.data?.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-smooth-blue font-bold text-[1.6rem] underline"
            >
              ユーザー認証(user_auth)を参照・編集
            </a>
          </Card>
        </section>

        <section className={styles.section}>
          <Card className={styles.rowList}>
            <Link
              href={{
                pathname: `/admin/chat`,
                query: {
                  user_id: userId,
                },
              }}
              target="_blank"
            >
              <BiChat style={{ width: 20, height: 20, marginRight: 8 }} />
            </Link>
          </Card>
        </section>

        <section className={styles.section}>
          <h2 className={styles.title}>
            Proposal（{proposalsSWR?.data?.length || 0}）
            <BorderedButton
              onClick={(e) => {
                e.preventDefault()
                setEditingId((c) => {
                  if (c === 0) return null
                  return 0
                })
              }}
              style={{ marginLeft: "1rem", background: "#ffffff" }}
            >
              新規作成
            </BorderedButton>
          </h2>
          {proposalsSWR.data && (
            <div>
              {sortById(proposalsSWR.data ?? [])?.map((proposal) => {
                return (
                  <Card
                    key={proposal.id}
                    className={styles.rowList}
                    style={{ marginBottom: "1rem" }}
                  >
                    <Proposal proposal={proposal} mutateProposals={mutateProposals} />
                  </Card>
                )
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
